import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import mapboxgl from 'mapbox-gl';
import { ArrowLeft, Home, MapPin, Map, Users, Layers } from "lucide-react";
import { slugify } from "../utils/slugify.js";
import projects from "../data/projects.json";


mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapStyle, setMapStyle] = useState("satellite-streets-v12");

  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const foundProject = projects.find(p => slug === slugify(p.name));
    if (foundProject) setProject(foundProject);
    setLoading(false);
  }, [slug]);

  useEffect(() => {
    if (!project || map.current) return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/" + mapStyle,
      center: [project.lng, project.lat],
      zoom: 13,
      interactive: true,
      scrollZoom: false
    });
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-left');

    map.current.on('load', () => {
      const el = document.createElement("div");
      el.className = "mapbox-marker-ping";
      el.setAttribute("aria-label", `Location marker for ${project.name}`);
      el.setAttribute("tabindex", "0");
      el.setAttribute("role", "button");

      marker.current = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([project.lng, project.lat])
        .addTo(map.current);
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [project, mapStyle]);

  const toggleMapStyle = () => {
  const newStyle = mapStyle === "satellite-streets-v12" ? "streets-v12" : "satellite-streets-v12";
  setMapStyle(newStyle);

  if (!map.current || !project) return;

  // remove old marker DOM node if it exists
  if (marker.current) {
    marker.current.remove();
    marker.current = null;
  }

  map.current.setStyle(`mapbox://styles/mapbox/${newStyle}`);

  map.current.once('style.load', () => {
    const el = document.createElement("div");
    el.className = "mapbox-marker-ping";
    el.setAttribute("aria-label", `Location marker for ${project.name}`);
    el.setAttribute("tabindex", "0");
    el.setAttribute("role", "button");

    marker.current = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([project.lng, project.lat])
      .addTo(map.current);
  });
};



  if (loading) return <div className="flex justify-center items-center h-[70vh]">Loading...</div>;
  if (!project) return (
    <div className="pt-20 px-6 max-w-4xl mx-auto text-center py-20">
      <h1 className="text-2xl font-bold mb-4">Project Not Found</h1>
      <button onClick={() => navigate('/projects')} className="mt-6 bg-blue-900 text-white px-4 py-2 rounded">Back to Projects</button>
    </div>
  );

  const typeString = project.type?.toLowerCase() || "";
  const isResidential = typeString.includes("residential");
  const isCommercial = typeString.includes("commercial");
  const isMultifamily = typeString.includes("multifamily");

  return (
    <>
      <Helmet>
        <title>{project.name} | CovePoint Development</title>
        <meta name="description" content={`Details about ${project.name}, a ${project.type} project by CovePoint.`} />
        <meta property="og:title" content={`${project.name} | CovePoint Development`} />
        <meta property="og:description" content={`Explore details about ${project.name}, a ${project.type} project in ${project.county} County, ${project.state}.`} />
        <meta property="og:image" content={`https://covepoint.com${project.image || '/share.jpg'}`} />
        <meta property="og:url" content={`https://covepoint.com/projects/${slug}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`https://covepoint.com/projects/${slug}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Place",
            "name": project.name,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": project.county + " County",
              "addressRegion": project.state
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": project.lat,
              "longitude": project.lng
            },
            "image": `https://covepoint.com${project.image || '/share.jpg'}`,
            "url": `https://covepoint.com/projects/${slug}`
          })}
        </script>
      </Helmet>

      <main className="pb-16 bg-gray-50 overflow-x-hidden">
        <div className="w-full h-[60vh] bg-cover bg-center relative" style={{ backgroundImage: `url(${project.image})` }}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full p-6 text-white">
            <div className="container mx-auto max-w-6xl">
              <button onClick={() => navigate('/projects')} className="flex items-center text-white mb-4 hover:underline">
                <ArrowLeft size={20} className="mr-2" /> Back to Projects
              </button>
              <h1 className="text-4xl sm:text-5xl font-bold mb-2">{project.name}</h1>
              <div className="flex items-center text-lg">
                <MapPin size={20} className="mr-1" />
                <span>{project.county} County, {project.state}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto max-w-6xl px-4 sm:px-6 -mt-16 relative z-10">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6 sm:p-10">
              <div className="grid grid-cols-3 gap-6 mb-10">
                <div className="flex items-center">
                  <Users size={24} className="text-blue-500 mr-3" />
                  <div>
                    <div className="text-sm text-gray-500">Project Type</div>
                    <div className="text-xl font-bold capitalize">{project.type || "-"}</div>
                  </div>
                </div>
                {isResidential && (
                  <div className="flex items-center">
                    <Home size={24} className="text-blue-500 mr-3" />
                    <div>
                      <div className="text-sm text-gray-500">Total Lots</div>
                      <div className="text-xl font-bold">{project.lots ? project.lots.toLocaleString() : "-"}</div>
                    </div>
                  </div>
                )}
                {isMultifamily && (
                  <div className="flex items-center">
                    <Home size={24} className="text-blue-500 mr-3" />
                    <div>
                      <div className="text-sm text-gray-500">Units (Keys)</div>
                      <div className="text-xl font-bold">{project.units ? project.units.toLocaleString() : "-"}</div>
                    </div>
                  </div>
                )}
                {isCommercial && (
                  <div className="flex items-center">
                    <Home size={24} className="text-blue-500 mr-3" />
                    <div>
                      <div className="text-sm text-gray-500">Square Feet</div>
                      <div className="text-xl font-bold">{project.squarefeet ? project.squarefeet.toLocaleString() + " SF" : "-"}</div>
                    </div>
                  </div>
                )}
                <div className="flex items-center">
                  <Map size={24} className="text-blue-500 mr-3" />
                  <div>
                    <div className="text-sm text-gray-500">Total Acreage</div>
                    <div className="text-xl font-bold">{project.acreage ? `${project.acreage.toLocaleString()} ac` : "–"}</div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-600">Location</h2>
                <a
                  href={`https://www.google.com/maps?q=${project.lat},${project.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 underline hover:text-blue-900"
                >
                  {project.address}
                </a>
              </div>

              <div className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-blue-900">Overview</h2>
                <p className="text-gray-700 leading-relaxed">
                  {project.overview || `${project.name} is a ${project.type} development in ${project.county} County, ${project.state}.`}
                </p>
              </div>

              {isResidential && project.features?.length > 0 && (
                <div className="mb-10">
                  <h2 className="text-2xl font-bold mb-4 text-blue-900">Community Features</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {project.features.map((feature, idx) => (
                      <div key={idx} className="bg-gray-50 p-4 rounded text-center text-sm font-medium text-gray-800">
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(isCommercial || isMultifamily) && (
                <div className="mb-10">
                  <h2 className="text-2xl font-bold mb-4 text-blue-900">Project Specs</h2>
                  <ul className="space-y-2 text-gray-700 list-disc list-inside">
                    {isCommercial && (
                      <li>Building Size: {project.squarefeet ? `${project.squarefeet.toLocaleString()} SF` : "—"}</li>
                    )}
                    {isMultifamily && (
                      <li>Units: {project.units ? project.units.toLocaleString() : "—"}</li>
                    )}
                  </ul>
                </div>
              )}

              <div>
                <h2 className="text-2xl font-bold mb-4 text-blue-900">Location Map</h2>
                <div className="h-[400px] bg-gray-100 rounded-lg overflow-hidden relative">
                  <div ref={mapContainer} className="w-full h-full" />
                  <button onClick={toggleMapStyle} className="absolute top-3 right-3 z-[1000] bg-white rounded-md shadow-md p-2">
                    <Layers className="h-6 w-6 text-blue-900" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
